import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Separator } from '@crowdfpl/ui-web/separator';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { isDuelSeoIndexableFromDuel } from '@crowdfpl/utils/duel-indexability';
import {
  buildDuelDescription,
  buildDuelJsonLdGraph,
  buildDuelTitleCore,
  computeDuelVoteSplit,
} from '@crowdfpl/utils/duel-seo';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getDuelById } from '@/api/duels';
import { DuelCard } from '@/components/duel-card';
import { AiPredictionsSection } from '@/components/duels/AiPredictionsSection';
import VotersSectionContainer from '@/components/duels/VotersSectionContainer';
import { LeagueDuelOverlay } from '@/components/leagues';
import SEO from '@/components/SEO';
import { ShareButtons } from '@/components/ShareButtons';
import { AppBar } from '@/components/shared/layout/app-bar';
import { GlobalHeader } from '@/components/shared/layout/global-header';
import { MobileBottomNavigation } from '@/components/shared/layout/mobile-bottom-navigation';
import { CDN_BASE_URL } from '@/constants';
import { trackUmamiEvent } from '@/helpers/analytics';
import { getAttributionSource } from '@/helpers/attribution';
import { roundPercentages } from '@/helpers/utils';
import { useAuth } from '@/hooks/auth';
import { useDuelVotes, useVoteOnDuel } from '@/hooks/data/duelVotes';
import { useGameweekStatuses } from '@/hooks/data/gameweekStatuses';
import { useIsMobileNav } from '@/hooks/utils/use-mobile';
import { useAuthModal } from '@/stores/ui';
import { useVotesStore } from '@/stores/votes';

export default function DuelDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileNav = useIsMobileNav();

  const { showAuthModal } = useAuthModal();
  const { isAuthenticated, user } = useAuth();
  const voteMutation = useVoteOnDuel();
  const { isVotingByDuelId, setVoting } = useVotesStore();

  const handleBack = () => {
    const state = location.state as { fromProfile?: boolean; profileUsername?: string; voteId?: string } | null;

    if (state?.fromProfile && state?.profileUsername) {
      // Return to profile with information to scroll to the specific vote
      navigate(`/profile/${state.profileUsername}`, {
        state: {
          fromDuel: true,
          voteId: state.voteId,
        },
      });
    } else {
      // Default behavior: go to duels page
      navigate('/duels');
    }
  };

  // Get gameweek information to check if it's live
  const { groupedGameweeks, currentGameweek } = useGameweekStatuses();

  const { data, isLoading } = useQuery({
    queryKey: ['duel', slug],
    queryFn: () => getDuelById(slug as string),
    enabled: !!slug,
  });

  // Fetch current user's vote for this duel only if logged in
  const userVoteQuery = useDuelVotes({
    duelId: data?.id,
    userId: user?.id,
    limit: 1,
    offset: 0,
    enabled: !!(data?.id && user?.id),
  });

  const duelGameweek = useMemo(() => {
    if (!data?.gameweek_id || !groupedGameweeks) return undefined;
    const allGameweeks = [
      ...(groupedGameweeks.current || []),
      ...(groupedGameweeks.upcoming || []),
      ...(groupedGameweeks.closed || []),
    ];
    return allGameweeks.find((gw) => gw.id === data.gameweek_id);
  }, [data?.gameweek_id, groupedGameweeks]);

  // Check if gameweek is live (is_current && now >= deadline) - must be called unconditionally
  const isLiveGameweek = Boolean(
    duelGameweek?.is_current &&
      duelGameweek.deadline_time &&
      Date.now() >= new Date(duelGameweek.deadline_time).getTime(),
  );

  // Latest Solio snapshot only — hide until we store per-GW history.
  const showSolioForDuel = Boolean(data?.gameweek_id && currentGameweek?.id && data.gameweek_id === currentGameweek.id);
  const gameweekNumber = duelGameweek?.number;

  // Вычисляем данные для выбранного игрока до ранних возвратов
  // Эти значения будут использоваться только если data существует, но хуки должны вызываться всегда
  const selectedSide = useMemo(() => {
    if (!data?.votes?.user_vote) return null;
    return data.votes.user_vote === data.player_a?.id
      ? 'player1'
      : data.votes.user_vote === data.player_b?.id
        ? 'player2'
        : null;
  }, [data?.votes?.user_vote, data?.player_a?.id, data?.player_b?.id]);

  const userSide = useMemo(() => {
    const userSelectedPlayerId = userVoteQuery.data?.duel_votes?.[0]?.selected_player_id || null;
    if (!userSelectedPlayerId || !data) return null;
    return userSelectedPlayerId === data.player_a?.id
      ? 'player1'
      : userSelectedPlayerId === data.player_b?.id
        ? 'player2'
        : null;
  }, [userVoteQuery.data, data]);

  const selectedForCard = useMemo(() => {
    return userSide || selectedSide;
  }, [userSide, selectedSide]);

  const leagueUserPick = useMemo((): 'player_a' | 'player_b' | null => {
    if (selectedForCard === 'player1') return 'player_a';
    if (selectedForCard === 'player2') return 'player_b';
    return null;
  }, [selectedForCard]);

  // Определяем имя выбранного игрока - должен быть вызван до ранних возвратов
  const chosenPlayerName = useMemo(() => {
    if (!data || !selectedForCard) return null;
    const paName = data.player_a?.web_name || 'Player A';
    const pbName = data.player_b?.web_name || 'Player B';
    if (selectedForCard === 'player1') return paName;
    if (selectedForCard === 'player2') return pbName;
    return null;
  }, [data, selectedForCard]);

  useEffect(() => {
    if (!data?.id) return;
    trackUmamiEvent('duel_detail_view', {
      duelId: data.id,
      source: getAttributionSource(),
    });
  }, [data?.id]);

  useEffect(() => {
    if (!data?.slug || !slug || slug === data.slug) {
      return;
    }

    navigate(`/duel/${data.slug}${location.search}`, { replace: true, state: location.state });
  }, [data?.slug, slug, location.search, location.state, navigate]);

  useEffect(() => {
    // could add analytics/scroll reset
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  // Добавляем tid параметр при открытии страницы для предотвращения кэширования
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has('tid')) {
      const timestamp = Date.now();
      searchParams.set('tid', timestamp.toString());
      // Preserve location.state when adding tid parameter
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
        state: location.state, // Preserve state from previous navigation
      });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader onAuthModalOpen={showAuthModal} />
        <AppBar showBack title="Duel Details" onBack={handleBack} />
        <div className={`container mx-auto px-4 py-6 max-w-6xl ${isMobileNav ? 'pb-20' : ''}`}>
          <Skeleton className="h-64 w-full mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
        {isMobileNav && <MobileBottomNavigation />}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader onAuthModalOpen={showAuthModal} />
        <AppBar showBack title="Duel Not Found" onBack={handleBack} />
        <div className={`container mx-auto px-4 py-6 max-w-6xl ${isMobileNav ? 'pb-20' : ''}`}>
          <Card>
            <CardContent className="flex items-center justify-center h-64">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-2">Duel not found</h2>
                <p className="text-muted-foreground">The duel you're looking for doesn't exist or has been removed.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        {isMobileNav && <MobileBottomNavigation />}
      </div>
    );
  }

  const paName = data.player_a?.web_name || 'Player A';
  const pbName = data.player_b?.web_name || 'Player B';
  const totalVotes = data.votes?.total_votes || 0;
  const aVotes = data.votes?.player_a_votes || 0;
  const bVotes = data.votes?.player_b_votes || 0;
  const [aPct, bPct] = roundPercentages(aVotes, bVotes);
  const paTeamShort = data.player_a?.team.short_name || '';
  const pbTeamShort = data.player_b?.team.short_name || '';
  const state: 'active' | 'locked' | 'partial' | 'resolved' | 'cancelled' =
    data.status === 'resolved' ? 'resolved'
      : data.status === 'locked' ? 'locked'
        : data.status === 'partial' ? 'partial'
          : data.status === 'cancelled' ? 'cancelled'
            : 'active';

  const handleVote = async (duelId: string, side: string) => {
    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    if (state === 'locked' || state === 'resolved' || state === 'partial' || state === 'cancelled') {
      return;
    }

    trackUmamiEvent('duel_detail_vote_click', {
      duelId,
      side,
      isAuthenticated,
    });

    const selected_player_id = side === 'player1' ? data.player_a?.id : data.player_b?.id;
    if (!selected_player_id) return;

    setVoting(duelId, true);
    try {
      await voteMutation.submitVote({ duel_id: duelId, selected_player_id });
    } finally {
      setVoting(duelId, false);
    }
  };

  const gwNum = data.gameweek_fpl_id ?? 0;
  const duelVoteSplit = computeDuelVoteSplit(aVotes, bVotes, totalVotes);
  const duelSeoTitle = buildDuelTitleCore(paName, pbName, gwNum);
  const duelSeoDescription = buildDuelDescription(paName, pbName, gwNum, duelVoteSplit);
  const duelSeoIndexable = isDuelSeoIndexableFromDuel(
    {
      votes: data.votes,
      player_a: data.player_a,
      player_b: data.player_b,
    },
    null,
  );
  const paFullName =
    data.player_a?.first_name && data.player_a?.second_name
      ? `${data.player_a.first_name} ${data.player_a.second_name}`
      : paName;
  const pbFullName =
    data.player_b?.first_name && data.player_b?.second_name
      ? `${data.player_b.first_name} ${data.player_b.second_name}`
      : pbName;
  const duelStructuredData = buildDuelJsonLdGraph({
    paShort: paName,
    pbShort: pbName,
    paName: paFullName,
    pbName: pbFullName,
    gw: gwNum,
    slug: data.slug,
    deadlineTime: data.deadline_time,
    voteSplit: duelVoteSplit,
  });

  // Упрощённый URL для OG-карточки с версией для сброса кэша
  // Используем tid из URL, если есть, иначе используем updated_at или текущее время
  const searchParams = new URLSearchParams(location.search);
  const tidParam = searchParams.get('tid');
  const cacheVersion = tidParam ? parseInt(tidParam, 10) : (data.updated_at ? new Date(data.updated_at).getTime() : Date.now());
  const ogImageUrl = data.og_card_url || `${CDN_BASE_URL}/og-cards/${data.id}.png?tid=${cacheVersion}`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={duelSeoTitle}
        description={duelSeoDescription}
        keywords={`${paName}, ${pbName}, FPL duel, Fantasy Premier League vote, ${paTeamShort}, ${pbTeamShort}`}
        type="article"
        image={ogImageUrl}
        imageWidth={1200}
        imageHeight={630}
        structuredData={duelStructuredData}
        canonicalPath={`/duel/${data.slug}`}
        robots={duelSeoIndexable ? 'index,follow' : 'noindex,follow'}
      />
      <GlobalHeader onAuthModalOpen={showAuthModal} />
      <AppBar
        showBack
        title={`${paName} vs ${pbName}`}
        breadcrumbs={!isMobileNav ? ['Duels', `${paName} vs ${pbName}`] : undefined}
        onBack={handleBack}
      />
      <div className={`container mx-auto px-4 py-6 max-w-6xl ${isMobileNav ? 'pb-20' : ''}`}>
        <div className="mb-6 flex justify-center">
          <div className="flex w-full max-w-md flex-col gap-4">
            <DuelCard
              duel={{
                id: data.id,
                slug: data.slug,
                player1: {
                  name: paName,
                  team: data.player_a?.team ? {
                    id: data.player_a.team.id,
                    name: data.player_a.team.name,
                    short_name: data.player_a.team.short_name,
                    code: data.player_a.team.code ? String(data.player_a.team.code) : '',
                  } : {
                    id: '',
                    name: '—',
                    short_name: '—',
                    code: '',
                  },
                  position: String({ 1: 'GK', 2: 'DEF', 3: 'MID', 4: 'FWD' }[data.player_a?.element_type || 0] || 'N/A'),
                  nowCost: data.player_a?.now_cost,
                  totalPoints: data.player_a?.total_points,
                  form: data.player_a?.form,
                  selectedByPercent: data.player_a?.selected_by_percent,
                  gameweek_stats: data.player_a?.gameweek_stats ? {
                    total_points: data.player_a.gameweek_stats.total_points ?? undefined,
                    is_final: data.player_a.gameweek_stats.is_final ?? undefined,
                    minutes: data.player_a.gameweek_stats.minutes ?? undefined,
                    fixtures: data.player_a.gameweek_stats.fixtures ?? undefined,
                  } : undefined,
                  availability_pct: data.player_a?.availability_pct ?? undefined,
                  availability_status: data.player_a?.availability_status ?? undefined,
                  availability_news: data.player_a?.availability_news ?? undefined,
                },
                player2: {
                  name: pbName,
                  team: data.player_b?.team ? {
                    id: data.player_b.team.id,
                    name: data.player_b.team.name,
                    short_name: data.player_b.team.short_name,
                    code: data.player_b.team.code ? String(data.player_b.team.code) : '',
                  } : {
                    id: '',
                    name: '—',
                    short_name: '—',
                    code: '',
                  },
                  position: String({ 1: 'GK', 2: 'DEF', 3: 'MID', 4: 'FWD' }[data.player_b?.element_type || 0] || 'N/A'),
                  nowCost: data.player_b?.now_cost,
                  totalPoints: data.player_b?.total_points,
                  form: data.player_b?.form,
                  selectedByPercent: data.player_b?.selected_by_percent,
                  gameweek_stats: data.player_b?.gameweek_stats ? {
                    total_points: data.player_b.gameweek_stats.total_points ?? undefined,
                    is_final: data.player_b.gameweek_stats.is_final ?? undefined,
                    minutes: data.player_b.gameweek_stats.minutes ?? undefined,
                    fixtures: data.player_b.gameweek_stats.fixtures ?? undefined,
                  } : undefined,
                  availability_pct: data.player_b?.availability_pct ?? undefined,
                  availability_status: data.player_b?.availability_status ?? undefined,
                  availability_news: data.player_b?.availability_news ?? undefined,
                },
                votes: { total_votes: totalVotes, player1_votes: aVotes, player2_votes: bVotes },
                potential_rating: data.potential_rating ?? undefined,
                ratingFactors: data.rating_factors
                  ? {
                    duelWeight: data.rating_factors.duel_weight,
                    duelWeightDetail: data.rating_factors.duel_weight_detail,
                    earlyBonusValue: data.rating_factors.early_bonus_value,
                    earlyBonusDetail: data.rating_factors.early_bonus_detail,
                    isDuelOfTheWeek: data.rating_factors.is_duel_of_the_week,
                    ratingFactorsDebug: data.rating_factors.rating_factors_debug,
                    humanVotesPlayerA: aVotes,
                    humanVotesPlayerB: bVotes,
                  }
                  : undefined,
              }}
              state={state}
              selectedPlayerId={selectedForCard}
              isAuthenticated={isAuthenticated}
              isVoting={!!(isVotingByDuelId as Record<string, boolean | undefined>)?.[data.id]}
              onVote={(id, side) => handleVote(id, side)}
              playerAPoints={data.player_a?.gameweek_stats?.total_points ?? undefined}
              playerBPoints={data.player_b?.gameweek_stats?.total_points ?? undefined}
              isLiveGameweek={isLiveGameweek}
              solioPredictedA={showSolioForDuel ? (data.player_a?.solio_projected_points ?? undefined) : undefined}
              solioPredictedB={showSolioForDuel ? (data.player_b?.solio_projected_points ?? undefined) : undefined}
              predictionOutcome={data.votes?.prediction_outcome ?? undefined}
              ratingChange={data.votes?.rating_change ?? undefined}
              isDuelOfTheWeek={data.is_duel_of_the_week ?? false}
              showDOTWBanner={data.is_duel_of_the_week ?? false}
            />
            {isAuthenticated ? (
              <LeagueDuelOverlay
                duelSlug={data.slug}
                duelId={data.id}
                player1Name={paName}
                player2Name={pbName}
                userPick={leagueUserPick}
              />
            ) : null}
          </div>
        </div>
        <div className="mb-4 flex justify-center">
          <ShareButtons
            duelSlug={data.slug}
            playerAName={paName}
            playerBName={pbName}
            gameweekNumber={gameweekNumber}
            playerAVotes={aVotes}
            playerBVotes={bVotes}
            totalVotes={totalVotes}
            state={state}
            chosenPlayerName={chosenPlayerName}
          />
        </div>
        {/* Hidden: FPL Core comparison URL is wrong
        {data.player_a?.fpl_id != null && data.player_b?.fpl_id != null && (
          <a
            href={`https://fplcore.com/comparison?players=${data.player_a.fpl_id}%2C${data.player_b.fpl_id}&slots=2`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 mt-3 mb-4 mx-auto w-fit max-w-sm px-4 py-2.5 rounded-lg border border-border/60 bg-card shadow-[0_1px_3px_0_hsl(var(--foreground)/0.04)] cursor-pointer transition-colors duration-[120ms] hover:bg-muted/50 active:bg-muted/70"
          >
            <div className="flex items-center justify-center h-9 w-9 rounded-md bg-muted/50 shrink-0">
              <img src="/icons/fpl_core_logo.svg" alt="FPL Core" className="h-8 w-8 rounded object-contain" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-foreground">Compare player stats</span>
              <span className="text-[11px] text-muted-foreground leading-tight">via FPL Core</span>
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors duration-[120ms] shrink-0 ml-2 px-2.5 py-1 rounded-md border border-border/60">
              Open
              <ExternalLink className="h-3 w-3" />
            </span>
          </a>
        )}
        */}
        <Separator className="my-6" />
        {/* Voters Section */}
        <VotersSectionContainer
          duelId={data.id}
          playerAId={data.player_a?.id || ''}
          playerBId={data.player_b?.id || ''}
          rawPlayerAId={data.player_a_id || ''}
          rawPlayerBId={data.player_b_id || ''}
          playerAName={paName}
          playerBName={pbName}
          aPct={aPct}
          bPct={bPct}
        />
        {data.ai_predictions && data.ai_predictions.length > 0 && data.player_a?.id && data.player_b?.id ? (
          <div className="mt-6">
            <AiPredictionsSection
              aiPredictions={data.ai_predictions}
              playerAId={data.player_a.id}
              playerBId={data.player_b.id}
              playerAName={paName}
              playerBName={pbName}
            />
          </div>
        ) : null}
      </div>
      {isMobileNav && <MobileBottomNavigation />}
    </div>
  );
}

